import clsx from 'clsx';
import HelpError from 'components/Form/HelpError';
import {RequiredType} from 'components/Form/types';
import {TooltipWrapperProps} from 'components/Tooltip/types';
import useLayout from 'hooks/useLayout';
import {FC, ReactNode} from 'react';
import {FieldError} from 'react-hook-form';
import {WithRequiredProperty} from 'types/utils';

import MaxLength from '../MaxLength';
import FieldLabel from './FieldLabel';

export type FieldProps = {
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    error?: FieldError;
    helpText?: ReactNode;
    label?: ReactNode;
    required?: boolean;
    requiredType?: RequiredType;
    tooltip?: WithRequiredProperty<TooltipWrapperProps, 'message'>;
} & (WithLabel | WithMaxLength | WithoutLabel | WithValue);

type WithLabel = {
    maxLength?: never;
    name: string;
    type: 'select';
};

type WithMaxLength = {
    maxLength?: number;
    name: string;
    type: 'checkbox' | 'input' | 'password' | 'textarea';
};

type WithoutLabel = {
    maxLength?: never;
    name?: never;
    type: 'button' | 'checkbox' | 'radio';
};

type WithValue = {
    maxLength?: never;
    name?: never;
    type: 'value';
};

const CENTER = {
    button: true,
    checkbox: false,
    input: true,
    password: false,
    radio: false,
    select: true,
    textarea: false,
    value: false,
};

const Field: FC<FieldProps> = ({
    children,
    className,
    disabled,
    error,
    helpText,
    label,
    maxLength,
    name,
    required,
    requiredType = 'text',
    tooltip,
    type,
}) => {
    const layout = useLayout();

    const sideBySide = layout === 'sideBySide';

    const helpError =
        helpText || error || maxLength ? (
            <HelpError
                disabled={disabled}
                error={error}
                helpText={helpText}
                label={label}
            >
                {maxLength && <MaxLength maxLength={maxLength} name={name} />}
            </HelpError>
        ) : null;

    const fieldLabel = label ? (
        <FieldLabel
            className={clsx(
                type === 'value' && sideBySide && 'text-right',
                !sideBySide && 'font-semibold',
            )}
            disabled={disabled}
            error={error}
            htmlFor={name}
            isSideBySide={sideBySide}
            required={required}
            requiredType={requiredType}
            tooltip={tooltip}
        >
            {label}
        </FieldLabel>
    ) : null;

    if (!sideBySide) {
        return (
            <div className={className} role="presentation">
                {fieldLabel}
                {children}
                {helpError}
            </div>
        );
    }

    return (
        <>
            {fieldLabel ? (
                <div
                    className={clsx(
                        'justify-self-end',
                        CENTER[type] && 'self-center',
                        type === 'password' && !required && 'pt-2.5',
                    )}
                    data-field-required={!!required}
                    data-field-type={type}
                    role="gridcell"
                >
                    {fieldLabel}
                </div>
            ) : (
                <div />
            )}
            <div role="gridcell">{children}</div>
            {helpError && <div />}
            {helpError}
        </>
    );
};

export default Field;
